import { Trans, useTranslation } from 'react-i18next';
import { useCallback, useContext } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@/UI/theme.exports';
import { Stack } from '@/UI/Stack/Stack';
import { H1, TextLgRg, Typography } from '@/UI/AppTypography/AppTypography';
import { Link } from '@/UI/Link/Link';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { color } from '@/colors';
import { Copyrights } from '@/components/Common/Copyrights';
import { I18N_KEYS } from '@/language/keys';
import { CopyIcon } from '@/UI/Icons/CopyIcon';
import { YouTubeLogo } from '@/UI/Icons/YouTubeLogo';
import { darkTheme } from '@/UI/theme';
import { DiscordLogo } from '@/UI/Icons/DiscordLogo';
import { useWithLoader } from '@/hooks/useWithLoader';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { AuthContext } from '@/contexts/AuthContext';
import { AppBanner } from '@/components/AppBanner/AppBanner';

const WEAVY_LINK = 'https://app.weavy.ai';
const COPY_LINK_TIMEOUT = 300;

export const MobileUnsupportedPage = () => {
  const { t } = useTranslation();
  const posthog = usePostHog();

  const copyLink = useCallback(async () => {
    posthog.capture('copy_link_mobile_unsupported_page');
    await navigator.clipboard.writeText(WEAVY_LINK);
    // To give impression of loading and then success
    await new Promise((resolve) => setTimeout(resolve, COPY_LINK_TIMEOUT));
  }, [posthog]);

  const { execute: handleCopyLinkClick, isLoading } = useWithLoader(copyLink, { sync: true });

  const handleDiscordClick = useCallback(() => {
    window.open(EXTERNAL_LINKS.discordInvite);
  }, []);

  const handleYouTubeClick = useCallback(() => {
    window.open('https://www.youtube.com/watch?v=YGx90x8XaHI');
  }, []);

  const { currentUser, isSigningIn } = useContext(AuthContext);
  const location = useLocation();

  if (location.pathname === '/' && !currentUser && !isSigningIn) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <AppBanner shouldDisplayClose={false} />
      <CssBaseline />
      <Stack
        direction="column"
        justifyContent="space-between"
        sx={{
          py: 3,
          pl: 3,
          pr: 4,
          bgcolor: color.Black100,
          // Fill available height with fallbacks
          minHeight: ['100vh', '-webkit-fill-available', '100dvh'],
          width: '100%',
        }}
        gap={1}
      >
        <WeavyLogoFilled title="Figma Weave Logo" style={{ width: 35, height: 35, color: color.White100 }} />
        <Stack direction="column" gap={6.75} justifyContent="space-between">
          <Stack direction="column" gap={3}>
            <H1 sx={{ fontSize: '2rem', whiteSpace: 'pre-wrap' }}>{t(I18N_KEYS.MOBILE_UNSUPPORTED_PAGE.TITLE)}</H1>
            <Stack direction="column" gap={1.5}>
              <TextLgRg>
                <Trans
                  i18nKey={I18N_KEYS.MOBILE_UNSUPPORTED_PAGE.SUBTITLE}
                  components={{
                    url: (
                      <Link
                        component="a"
                        href={WEAVY_LINK}
                        sx={{
                          color: color.White100,
                          textDecoration: 'underline',
                          textDecorationColor: color.White100,
                          '&:hover': {
                            textDecoration: 'none',
                          },
                        }}
                      />
                    ),
                  }}
                />
              </TextLgRg>
              <ButtonContained
                mode="outlined"
                size="medium"
                onClick={handleCopyLinkClick}
                loading={isLoading}
                sx={{ width: 'fit-content', gap: 0.5 }}
              >
                <CopyIcon title="Copy link" width={16} height={16} color={isLoading ? 'transparent' : color.White100} />
                <Typography variant="inherit" color={isLoading ? 'transparent' : color.White100}>
                  {t(I18N_KEYS.GENERAL.COPY_LINK)}
                </Typography>
              </ButtonContained>
            </Stack>
          </Stack>
          <Stack direction="column" gap={2}>
            <TextLgRg>{t(I18N_KEYS.MOBILE_UNSUPPORTED_PAGE.FOOTER_TEXT)}</TextLgRg>
            <Stack direction="column" gap={1.5}>
              <ButtonContained
                mode="filled-light-secondary"
                size="medium"
                sx={{ maxWidth: '400px', gap: 0.5 }}
                onClick={handleYouTubeClick}
              >
                <YouTubeLogo title="YouTube" width={20} height={20} />
                {t(I18N_KEYS.MOBILE_UNSUPPORTED_PAGE.YOUTUBE_INTRO_BUTTON)}
              </ButtonContained>
              <ButtonContained
                mode="filled-light-secondary"
                size="medium"
                sx={{ maxWidth: '400px', gap: 0.5 }}
                onClick={handleDiscordClick}
              >
                <DiscordLogo title="Discord" width={20} height={20} />
                {t(I18N_KEYS.MOBILE_UNSUPPORTED_PAGE.DISCORD_BUTTON)}
              </ButtonContained>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <Copyrights textColor={color.White100} />
        </Stack>
      </Stack>
    </ThemeProvider>
  );
};
